import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import { Menu } from 'antd';

import './MentionSuggestion.css';

export interface MentionItem {
  id: string;
  value: string;
  label: string;
  category: string;
}

interface MentionListProps {
  items: MentionItem[];
  command: (item: { id: string; label: string }) => void;
}

interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      // Pass only the value without the @ character
      props.command({ id: item.value, label: item.value });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }
      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }
      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }
      return false;
    },
  }));

  const menuItems = props.items.map((item, index) => ({
    key: item.id,
    label: (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ color: '#999', fontStyle: 'italic', minWidth: 70, fontSize: '13px' }}>
          {item.category}:
        </span>
        <span style={{ color: '#333', fontSize: '13px' }}>{item.label}</span>
      </div>
    ),
    onClick: () => selectItem(index),
  }));

  return (
    <div className="mention-dropdown-antd">
      {props.items.length ? (
        <Menu
          items={menuItems}
          selectedKeys={[props.items[selectedIndex]?.id]}
          style={{ border: 'none' }}
          mode="vertical"
        />
      ) : (
        <div style={{ padding: '6px 12px', color: '#999', fontSize: '13px' }}>No results</div>
      )}
    </div>
  );
});

MentionList.displayName = 'MentionList';

export const createMentionSuggestion = (getItems: () => MentionItem[]): Omit<SuggestionOptions<MentionItem>, 'editor'> => ({
  char: '@',
  items: ({ query }) => {
    const items = getItems(); // Get latest items from the getter function
    return items.filter((item) =>
      item.label.toLowerCase().includes(query.toLowerCase()) ||
      item.category.toLowerCase().includes(query.toLowerCase())
    );
  },
  command: ({ editor, range, props }) => {
    // Insert plain text instead of mention node
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent(props.id)
      .run();
  },
  render: () => {
    let component: ReactRenderer<MentionListRef> | null = null;
    let popup: TippyInstance[] | null = null;

    return {
      onStart: (props: SuggestionProps<MentionItem>) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
      },

      onUpdate(props: SuggestionProps<MentionItem>) {
        component?.updateProps(props);

        if (!props.clientRect) {
          return;
        }

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect,
        });
      },

      onKeyDown(props: { event: KeyboardEvent }) {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }

        return component?.ref?.onKeyDown(props) ?? false;
      },

      onExit() {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
});

export default MentionList;
